/**
 * Las pestañas del módulo de etiquetas. Qué pestañas son y por qué dependen
 * del tenant está en `tabs.ts`, que es donde vive la decisión.
 *
 * Debajo va el recorrido en una línea (#170): Orden → Rollo → Código →
 * Activación → Escaneo. Dos jerarquías conviven en este módulo —la comercial
 * (Producto › Lote) y la física (Orden › Rollo › Código)— y se cruzan recién
 * en la activación; hasta ahí un código existe pero no tiene lote, producto
 * ni vencimiento. Quien entra por primera vez no tiene por qué saberlo, y es
 * lo que explica por qué hay tantas pantallas relacionadas.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/ui/cn';
import { tabsFor, type TabId } from './tabs';

const TAB =
  'rounded border px-3 py-1.5 text-sm font-medium transition-colors focus:ring-2 focus:ring-accent/40 focus:outline-none';

export function LabelsTabs({ active }: { active: TabId }): ReactNode {
  const { t } = useI18n();
  const { claims } = useAuth();

  return (
    <div className="space-y-2">
      <nav aria-label={t('labels.title')} className="flex flex-wrap gap-2">
        {tabsFor(claims?.tenant).map((tab) => (
        <Link
          key={tab.id}
          to={tab.to}
          aria-current={active === tab.id ? 'page' : undefined}
          className={cn(
            TAB,
            active === tab.id
              ? 'border-accent bg-accent text-accent-contrast'
              : 'border-border-strong bg-surface text-ink hover:bg-surface-sunken',
          )}
        >
          {t(tab.labelKey)}
        </Link>
      ))}
      </nav>
      <p className="text-xs text-ink-subtle">{t('labels.journey')}</p>
    </div>
  );
}
